import gql from "graphql-tag";
import {useMemo} from "react";

import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {useQuery} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {useUserAttributes} from "@khan/shared-core";

import type {assignmentData_user_assignment_classroom as AssignmentDataClassroom} from "./__graphql-types__/assignmentData.ts";
import type {assignmentDataType} from "./__graphql-types__/index.ts";

export const USE_ASSIGNMENT_DATA: GqlOperation<
    assignmentDataType["response"],
    assignmentDataType["variables"]
> = gqlOp(gql`
    query assignmentData($assignmentID: String!) {
        user {
            id
            assignment(id: $assignmentID) {
                id
                instructions
                title
                itemCompletionStates {
                    studentKaid
                    completedOn
                    activitySubmissions {
                        threadID
                    }
                }
                classroom {
                    id
                    cacheId
                    name
                }
            }
        }
    }
`);

export interface StudentActivityAssignmentData {
    id: string;
    completedOn: string | null | undefined;
    threadIDs: string[] | null | undefined;
    title: string | null | undefined;
    classroom: AssignmentDataClassroom | null | undefined;
    instructions: string | null | undefined;
}

type Result =
    | {
          status: "loading";
      }
    | {
          status: "no-data";
      }
    | (StudentActivityAssignmentData & {
          status: "ready";
      });

export const useAssignmentData = (assignmentID: string): Result => {
    const [result] = useQuery(USE_ASSIGNMENT_DATA, {
        variables: {assignmentID},
        fetchPolicy: FetchPolicy.NetworkOnly,
        skip: !assignmentID,
    });

    const userAttributes = useUserAttributes();

    const value = useMemo<Result>(() => {
        if (userAttributes === null) {
            return {status: "no-data"};
        }

        const kaid = userAttributes.kaid;

        if (result.status === "loading") {
            return {status: "loading"};
        }

        if (result.status !== "success") {
            return {status: "no-data"};
        }

        // If the user has no assignment associated with this ID (assignment
        // does not exist, or the user is requesting an assignment that is not
        // assigned to them), return no-data
        if (!result.data.user!.assignment) {
            return {status: "no-data"};
        }

        const studentCompletionState =
            result.data.user!.assignment!.itemCompletionStates?.find(
                (itemCompleteState) => itemCompleteState?.studentKaid === kaid,
            );
        const completedOn = studentCompletionState?.completedOn;
        const threadIDs = studentCompletionState?.activitySubmissions?.map(
            (submission) => submission!.threadID,
        );
        return {
            status: "ready",
            id: result.data.user!.assignment!.id,
            completedOn: completedOn,
            threadIDs: threadIDs,
            title: result.data.user!.assignment!.title,
            classroom: result.data.user!.assignment!.classroom,
            instructions: result.data.user!.assignment!.instructions,
        };
    }, [result, userAttributes]);

    return value;
};
